import { type ChangeEvent, type FormEvent, useMemo, useState } from 'react';

import { Alert, Box, Button, createTheme, Stack, TextField, ThemeProvider, Typography } from '@mui/material';
import i18n from 'i18n-react';
import { Redirect } from 'react-router-dom';

import { useConfirmImpersonation } from 'hooks/useConfirmImpersonation';
import { useEndImpersonation } from 'hooks/useEndImpersonation';
import { useImpersonate } from 'hooks/useImpersonate';
import { useImpersonationAdminCheck } from 'hooks/useImpersonationAdminCheck';
import { useImpersonationPreview } from 'hooks/useImpersonationPreview';
import { useImpersonationState } from 'hooks/useImpersonationState';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import c from './_impersonateView.module.scss';

const DEFAULT_PRIMARY_COLOR = '#0e2e50';

type StatusType = 'success' | 'error' | 'info';

interface ImpersonateViewUserInfo {
  loaded: boolean;
  user: { email?: string } | null;
}

export function ImpersonateView() {
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<StatusType>('info');
  const [previewUser, setPreviewUser] = useState<Record<string, unknown> | null>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  const { loaded, user } = useUserInfo() as unknown as ImpersonateViewUserInfo;
  const { appId, appInfo } = useSettings();
  const adminCheck = useImpersonationAdminCheck(loaded);
  const { state: impersonationState } = useImpersonationState();

  const impersonate = useImpersonate();
  const preview = useImpersonationPreview();
  const confirm = useConfirmImpersonation();
  const endImpersonation = useEndImpersonation();

  const theme = useMemo(() => {
    return createTheme({ palette: { primary: { main: appInfo.primaryColor ?? DEFAULT_PRIMARY_COLOR } } });
  }, [appInfo.primaryColor]);

  if (loaded && !user?.email) {
    return <Redirect to="/login" />;
  }

  if (adminCheck.isSuccess && adminCheck.data === false) {
    return <Redirect to="/main" />;
  }

  if (!loaded || adminCheck.isPending) {
    return null;
  }

  const isBusy = impersonate.isPending || preview.isPending || confirm.isPending || endImpersonation.isPending;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);
    setPreviewUser(null);
    setPendingToken(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatusType('error');
      setStatusMessage(i18n.translate('impersonateView.missingEmail') as string);
      return;
    }

    impersonate.mutate(trimmedEmail, {
      onSuccess: ({ token }) => {
        setStatusType('success');
        setStatusMessage(i18n.translate('impersonateView.tokenFetched', { email: trimmedEmail }) as string);
        setPendingToken(token);

        if (!appId) return;

        preview.mutate(
          { appId, token },
          {
            onSuccess: (result) => {
              setPreviewUser(result);
            },
            onError: () => {
              setStatusType('error');
              setStatusMessage(i18n.translate('impersonateView.previewFailed') as string);
            },
          },
        );
      },
      onError: () => {
        setStatusType('error');
        setStatusMessage(i18n.translate('impersonateView.startFailed') as string);
      },
    });
  };

  const handleConfirm = () => {
    if (!pendingToken || !email || previewUser === null) return;

    confirm.mutate(
      { token: pendingToken, email: email.trim(), user: previewUser },
      {
        onSuccess: () => {
          window.location.href = '/main';
        },
        onError: () => {
          setStatusType('error');
          setStatusMessage(i18n.translate('impersonateView.confirmFailed') as string);
        },
      },
    );
  };

  const handleEnd = () => {
    setStatusMessage(null);

    endImpersonation.mutate(undefined, {
      onSuccess: () => {
        window.location.href = '/main';
      },
      onError: () => {
        setStatusType('error');
        setStatusMessage(i18n.translate('impersonateView.endFailed') as string);
      },
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={c.root}>
        <Box className={c.card}>
          {impersonationState.active && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {i18n.translate('impersonateView.currentlyImpersonating', { email: impersonationState.email })}
              </Typography>
            </Alert>
          )}

          <Typography variant="h5" component="h1" gutterBottom>
            {i18n.translate('impersonateView.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {i18n.translate('impersonateView.description')}
          </Typography>

          <Box sx={{ mt: 1, mb: 2 }}>
            <Button
              type="button"
              variant="text"
              color="primary"
              sx={{ '&:hover': { color: 'primary.main', textDecoration: 'none !important' } }}
              onClick={() => {
                window.location.href = '/main';
              }}
            >
              {i18n.translate('impersonateView.returnToApp')}
            </Button>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
            <TextField
              label={i18n.translate('impersonateView.emailLabel') as string}
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              disabled={isBusy}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ '&:hover': { color: 'primary.contrastText', textDecoration: 'none !important' } }}
                disabled={isBusy}
              >
                {impersonate.isPending
                  ? i18n.translate('impersonateView.impersonating')
                  : i18n.translate('impersonateView.startImpersonation')}
              </Button>

              <Button
                type="button"
                variant="outlined"
                color="primary"
                sx={{ '&:hover': { color: 'primary.main', textDecoration: 'none !important' } }}
                onClick={handleConfirm}
                disabled={pendingToken === null || confirm.isPending}
              >
                {confirm.isPending
                  ? i18n.translate('impersonateView.confirming')
                  : i18n.translate('impersonateView.confirmImpersonation')}
              </Button>

              {impersonationState.active && (
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  sx={{ '&:hover': { color: 'inherit', textDecoration: 'none !important' } }}
                  onClick={handleEnd}
                  disabled={isBusy}
                >
                  {endImpersonation.isPending
                    ? i18n.translate('impersonateView.ending')
                    : i18n.translate('impersonateView.endImpersonation')}
                </Button>
              )}
            </Stack>
          </Box>

          {statusMessage && (
            <Alert severity={statusType} sx={{ mt: 3 }}>
              {statusMessage}
            </Alert>
          )}

          {previewUser !== null && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {i18n.translate('impersonateView.previewLabel')}
              </Typography>
              <Box component="pre" className={c.previewPre}>
                <code>{JSON.stringify(previewUser, null, 2)}</code>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
